import { useGroupMemberHook, GroupMemberHookReturn, GroupData } from './useGroupMemberHook';
import SamaritanFetch from '../../../api/httpClient';
import { Deployments } from '../../../types';
import { SamaritanMemberSmallTemplate } from './useAdminMemberSearch';

// TODO: Import actual API functions when they're created
// import { fetchDeploymentGroups, fetchDeploymentMembers } from 'api/deployments';

const transformDeploymentToGroupData = (deployment: Deployments): GroupData[] => {
    return deployment.relations?.phases.map(phase => {
        return {
            id: phase.id,
            title: deployment.name,
            tags: (() => {
                const now = new Date();
                const start = new Date(phase.start_date);
                const end = new Date(phase.end_date);
                return [[phase.name || 'Phase ' + phase.id, now >= start && now <= end ? 'green' : 'red']];
            })(),
            stats: [
                ['Seat Count', phase.seat_total],
                 ['LIVE Members', phase.live_members_count || 0], 
                 ['PENDING Members', phase.pending_members_count || 0], 
                 ['UNENROLLED Members', phase.inactive_members_count || 0],
                 ['ELIGIBLE/TARGET Members', phase.eligibility_and_target_list_members_count || 0]
            ],
            startDate: phase.start_date,
        };
    }) || [];
};

const fetchDeploymentGroups = async (): Promise<Response> => {
    const response = await SamaritanFetch.v3.get('/deployments?deployment[relations][phases]{}');
    
    if (!response.ok) {
        return response; // Let the error handling in useGroupMemberHook deal with it
    }
    
    const data = await response.json();
    
    // Handle different possible response structures
    const deployments: Deployments[] = Array.isArray(data) ? data : data.deployments || [];
    const groups: GroupData[] = deployments.flatMap(transformDeploymentToGroupData)
        .sort((a, b) => {
            const aActive = a.tags.some(([_, type]) => type === 'green');
            const bActive = b.tags.some(([_, type]) => type === 'green');
            if (aActive !== bActive) return bActive ? 1 : -1;
            return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
        });
    
    // Override json() to return transformed data
    response.json = () => Promise.resolve({ groups });
    return response;
};

const fetchDeploymentMembers = async (groupId: number): Promise<Response> => {
    const response = await SamaritanFetch.v3.get(`/samaritan_members?template=small&samaritan_members[phase_id][]=${groupId}`);
    
    if (!response.ok) {
        return response;
    }
    
    const data = await response.json();
    
    // Transform the response to match expected format
    const members = (data.samaritan_members || []).sort((a: SamaritanMemberSmallTemplate, b: SamaritanMemberSmallTemplate) => {
        const aIsLive = a.profile_stage === 'LIVE' || a.profile_stage === 'NEEDS_REVIEW';
        const bIsLive = b.profile_stage === 'LIVE' || b.profile_stage === 'NEEDS_REVIEW';
        return aIsLive === bIsLive ? 0 : aIsLive ? -1 : 1;
    });
    
    // Override json() to return transformed data
    response.json = () => Promise.resolve({ members });
    return response;
};

export const useDeploymentMemberGroup = (): GroupMemberHookReturn => {
    return useGroupMemberHook(fetchDeploymentGroups, fetchDeploymentMembers);
};

export default useDeploymentMemberGroup; 